import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  DownloadIcon,
  ExitIcon,
  HamburgerMenuIcon,
  HomeIcon,
  PersonIcon,
} from "@radix-ui/react-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PropsWithChildren, ReactNode } from "react";
import {
  Link as RouterLink,
  NavLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import useAppLogo from "../assets/use-app-icon.png";
import { authClient } from "../authentication/core/client/auth-client";
import { buildLoginRedirectPath } from "../authentication/core/client/login-redirect";
import { routes } from "../routes";
import {
  getAdminContext,
  getApiUserErrorMessage,
  isForbiddenApiError,
  isUnauthorizedApiError,
} from "../shared/api/nest-api";
import { AdminContextState, useAdminContext } from "./admin-context";

const sidebarWidth = "256px";

export function AdminRoot() {
  const location = useLocation();
  const adminContextQuery = useQuery({
    queryKey: ["admin", "context"],
    queryFn: getAdminContext,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: "always",
  });

  if (adminContextQuery.isPending) {
    return <AdminLoadingScreen />;
  }

  if (adminContextQuery.error) {
    if (isUnauthorizedApiError(adminContextQuery.error)) {
      return (
        <Navigate
          to={buildLoginRedirectPath(`${location.pathname}${location.search}`)}
          replace
        />
      );
    }

    if (isForbiddenApiError(adminContextQuery.error)) {
      return <AdminAccessDenied />;
    }

    return (
      <AdminErrorScreen
        message={
          getApiUserErrorMessage(adminContextQuery.error) ??
          "Der Adminbereich konnte nicht geladen werden."
        }
        onRetry={() => void adminContextQuery.refetch()}
      />
    );
  }

  return (
    <AdminContextState.Provider value={adminContextQuery.data}>
      <AdminShell>
        <Outlet />
      </AdminShell>
    </AdminContextState.Provider>
  );
}

export function RequiresSuperAdmin({ children }: PropsWithChildren) {
  const { role } = useAdminContext();

  return role === "superAdmin" ? children : <AdminSectionForbidden />;
}

function AdminShell({ children }: PropsWithChildren) {
  const navigation = useDisclosure();
  const { role } = useAdminContext();
  const sessionQuery = authClient.useSession();
  const navigate = useNavigate();
  const signOutMutation = useMutation({
    mutationFn: () => authClient.signOut(),
    onSuccess: () => {
      void navigate(routes.auth.login, { replace: true });
    },
  });
  const navigationItems: {
    label: string;
    path: string;
    icon: ReactNode;
  }[] = [
    {
      label: "Stationen",
      path: routes.admin.stations,
      icon: <HomeIcon aria-hidden />,
    },
    {
      label: "Exporte",
      path: routes.admin.exports,
      icon: <DownloadIcon aria-hidden />,
    },
    ...(role === "superAdmin"
      ? [
          {
            label: "Administratoren",
            path: routes.admin.admins,
            icon: <PersonIcon aria-hidden />,
          },
        ]
      : []),
  ];
  const email = sessionQuery.data?.user.email;
  const roleLabel = role === "superAdmin" ? "Super Admin" : "Admin";
  const handleSignOut = () => signOutMutation.mutate();

  return (
    <Box minH="100vh" bg="gray.50">
      <Flex
        as="header"
        display={{ base: "flex", md: "none" }}
        align="center"
        justify="space-between"
        h="64px"
        px="4"
        bg="white"
        borderBottomWidth="1px"
        position="sticky"
        top="0"
        zIndex="sticky"
      >
        <AdminBrand compact />
        <IconButton
          aria-label="Navigation öffnen"
          aria-expanded={navigation.isOpen}
          icon={<HamburgerMenuIcon />}
          variant="ghost"
          onClick={navigation.onOpen}
        />
      </Flex>

      <Box
        as="aside"
        display={{ base: "none", md: "block" }}
        w={sidebarWidth}
        h="100vh"
        bg="white"
        borderRightWidth="1px"
        position="fixed"
        insetY="0"
        left="0"
      >
        <AdminNavigation
          navigationItems={navigationItems}
          roleLabel={roleLabel}
          email={email}
          isSigningOut={signOutMutation.isPending}
          onNavigate={() => undefined}
          onSignOut={handleSignOut}
        />
      </Box>

      <Drawer
        isOpen={navigation.isOpen}
        placement="left"
        onClose={navigation.onClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton aria-label="Navigation schließen" />
          <DrawerHeader borderBottomWidth="1px">
            <AdminBrand compact />
          </DrawerHeader>
          <DrawerBody p="0">
            <AdminNavigation
              navigationItems={navigationItems}
              roleLabel={roleLabel}
              email={email}
              isSigningOut={signOutMutation.isPending}
              onNavigate={navigation.onClose}
              onSignOut={handleSignOut}
              showBrand={false}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box as="main" minH="100vh" ml={{ base: "0", md: sidebarWidth }}>
        <Box
          w="100%"
          maxW="container.xl"
          mx="auto"
          px={{ base: "4", sm: "6", lg: "8", xl: "10" }}
          py={{ base: "6", md: "10" }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function AdminNavigation({
  navigationItems,
  roleLabel,
  email,
  isSigningOut,
  onNavigate,
  onSignOut,
  showBrand = true,
}: {
  navigationItems: { label: string; path: string; icon: ReactNode }[];
  roleLabel: string;
  email?: string;
  isSigningOut: boolean;
  onNavigate: () => void;
  onSignOut: () => void;
  showBrand?: boolean;
}) {
  return (
    <Flex direction="column" h="100%" p="4">
      {showBrand && <AdminBrand />}

      <Stack as="nav" aria-label="Admin Navigation" spacing="1" mt="8">
        {navigationItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            style={{ textDecoration: "none" }}
            onClick={onNavigate}
          >
            {({ isActive }) => (
              <HStack
                spacing="3"
                px="3"
                py="2.5"
                borderRadius="md"
                bg={isActive ? "blue.50" : "transparent"}
                color={isActive ? "blue.700" : "gray.700"}
                fontWeight={isActive ? "semibold" : "medium"}
                _hover={{ bg: isActive ? "blue.50" : "gray.100" }}
              >
                {item.icon}
                <Text>{item.label}</Text>
              </HStack>
            )}
          </NavLink>
        ))}
      </Stack>

      <Stack mt="auto" pt="4" borderTopWidth="1px" spacing="3">
        <Box minW="0">
          <Text fontSize="sm" fontWeight="semibold">
            {roleLabel}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {email}
          </Text>
        </Box>
        <Button
          leftIcon={<ExitIcon />}
          variant="outline"
          size="sm"
          isLoading={isSigningOut}
          onClick={onSignOut}
        >
          Ausloggen
        </Button>
      </Stack>
    </Flex>
  );
}

function AdminBrand({ compact = false }: { compact?: boolean }) {
  return (
    <HStack
      as={RouterLink}
      to={routes.admin.stations}
      spacing="3"
      color="gray.900"
      textDecoration="none"
      aria-label={compact ? "Use Admin Startseite" : undefined}
      _hover={{ textDecoration: "none" }}
    >
      <Image src={useAppLogo} alt="" boxSize={compact ? "32px" : "40px"} />
      <Box>
        <Text fontWeight="bold" lineHeight="short">
          Use Admin
        </Text>
        {!compact && (
          <Text color="gray.500" fontSize="xs">
            Station Control
          </Text>
        )}
      </Box>
    </HStack>
  );
}

function AdminLoadingScreen() {
  return (
    <Center minH="100vh" bg="gray.50" px="6" aria-live="polite">
      <Stack align="center" spacing="4">
        <Spinner size="lg" />
        <Text color="gray.600">Adminbereich wird geladen …</Text>
      </Stack>
    </Center>
  );
}

function AdminAccessDenied() {
  const sessionQuery = authClient.useSession();

  return (
    <Center minH="100vh" bg="gray.50" px="6">
      <Stack align="center" spacing="5" textAlign="center" maxW="lg">
        <Heading size="xl">Kein Admin-Zugriff</Heading>
        <Text color="gray.600">
          Das Konto <strong>{sessionQuery.data?.user.email}</strong> hat keine
          Berechtigung für diesen Bereich.
        </Text>
        <HStack spacing="3">
          <Button as={RouterLink} to={routes.account} colorScheme="blue">
            Zum Konto
          </Button>
          <Button as={RouterLink} to={routes.home} variant="outline">
            Zur Startseite
          </Button>
        </HStack>
      </Stack>
    </Center>
  );
}

function AdminSectionForbidden() {
  return (
    <Stack spacing="5" align="flex-start" maxW="container.md">
      <Heading size="xl">Dieser Bereich ist nicht freigegeben.</Heading>
      <Text color="gray.600">
        Stationsadministratoren können ausschließlich ihre zugewiesenen
        Stationen und Exporte verwalten.
      </Text>
      <Button as={RouterLink} to={routes.admin.stations} colorScheme="blue">
        Zu meinen Stationen
      </Button>
    </Stack>
  );
}

function AdminErrorScreen({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Center minH="100vh" bg="gray.50" px="6">
      <Stack align="center" spacing="5" textAlign="center" maxW="lg">
        <Heading size="xl">Adminbereich nicht verfügbar</Heading>
        <Text color="gray.600">{message}</Text>
        <Button colorScheme="blue" onClick={onRetry}>
          Erneut versuchen
        </Button>
      </Stack>
    </Center>
  );
}
